import React from 'react'
import { useNavigate } from 'react-router-dom'

import { TimeframeBar } from '../../components'
import { SpecificChartFunctionality, TIMEFRAME } from '../../constants/common'
import { useModelState } from '../../model-hooks/use-model-state'
import Chart from '../../models/chart'
import { CustomDateRange } from './custom-date-range'
import { DailyChangeDisplay } from './daily-change-display'
import Search from './search'
import { useChartControls } from './use-chart-controls'
import { useFavoriteTimeframes } from './use-favorite-timeframes'
import { useTimeframeDateRangeGroups } from './use-timeframe-date-range-groups'

interface SettingsComponentProps {
  chartModel: Chart
  chartIndex: number
}

export function ChartControls({ chartIndex, chartModel: unwatchedChartModel }: SettingsComponentProps) {
  const navigate = useNavigate()
  const { favoriteTimeframes, toggleFavoriteTimeframe } = useFavoriteTimeframes()
  const chartModel = useModelState(unwatchedChartModel, {
    watchProperties: ['ticker'],
  })
  const {
    customDateRangeDialogState,
    handleDateRangeSelect,
    generateChartLayoutRouterPath,
    dateRangeGroups,
    timeFrameGroups,
  } = useTimeframeDateRangeGroups({
    isPremium: window.FinvizSettings.hasUserPremium,
    chartModel,
    specificChartFunctionality: SpecificChartFunctionality.chartPage,
  })
  const { isLoading } = useChartControls({
    chartModel,
    isInteractive: true,
  })

  const handleTickerSelect = React.useCallback(
    (ticker: string) => {
      const upperCaseTicker = ticker.toUpperCase()

      navigate(
        generateChartLayoutRouterPath({
          ticker: upperCaseTicker,
          timeframe: chartModel.timeframe,
          dateRange: chartModel.dateRange,
        })
      )
    },
    [chartModel, generateChartLayoutRouterPath, navigate]
  )

  return (
    <div className="relative flex h-11 space-x-2 bg-white px-2 dark:bg-gray-800">
      {chartModel.chart_layout().canChangeTicker && (
        <Search
          ticker={chartModel.ticker}
          instrument={chartModel.instrument}
          onTickerSelected={handleTickerSelect}
          chartIndex={chartIndex}
        />
      )}

      <TimeframeBar
        size="medium"
        chartIndex={chartIndex}
        timeFrameGroups={timeFrameGroups}
        dateRangeGroups={dateRangeGroups}
        selectedTimeframe={chartModel.timeframe}
        onTimeframeSelect={() => {}}
        selectedDateRange={chartModel.dateRange}
        favoriteTimeframes={favoriteTimeframes}
        loadingTimeframe={isLoading ? chartModel.timeframe : undefined}
        onDateRangeSelect={handleDateRangeSelect}
        onFavoriteTimeframeToggle={({ value }: { value: TIMEFRAME }) => {
          toggleFavoriteTimeframe(value)
        }}
      />

      <DailyChangeDisplay chartIndex={chartIndex} />

      <CustomDateRange
        dialogState={customDateRangeDialogState}
        onSave={handleDateRangeSelect}
        chartModel={chartModel}
      />
    </div>
  )
}
